import { makeApolloClient } from './makeApolloClient'
import { t } from '@lingui/macro'
import { print } from 'graphql'
import { onError, ErrorResponse } from '@apollo/client/link/error'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import fetch from 'unfetch'
import { captureException } from '@sentry/browser'
import { notification } from '../components/common/notifications/Notifications'
import gqlPrivateSchemaFragments from './gqlPrivateSchemaFragments'
import ReactDOM from 'react-dom'

import debug from 'debug'
import environment from '../../../shared/environment'
import { ApolloLink, ServerError } from '@apollo/client'
import { createNetworkStatusNotifier } from 'react-apollo-network-status'
import { isSSR } from 'shared/helpers'

const logPrivate = debug('looop:apolloClientPrivate')
const logPublic = debug('looop:apolloClientPublic')
import { LOGIN_PAGE_ERROR_LS_KEY } from '../pages/auth/login/LoginPage'
import { camelCase } from 'lodash'
import { i18n } from '@lingui/core'
import {
  SessionDocument,
  SessionQuery
} from 'front-end/src/public-api/hooks/usePublicApiSession.codegen'

const { link, useApolloNetworkStatus } = createNetworkStatusNotifier()
export { useApolloNetworkStatus }

export function handleApiErrors(graphQLErrors, operation) {
  if (graphQLErrors) {
    graphQLErrors.map((error) => {
      const { query, variables } = operation
      console.error(
        `[GraphQL error]: Variables: `,
        variables,
        ` query: ${print(query)}`
      )

      // public API uses messageLocalisations whereas private API sends a single messageLocalisation for the current user
      const { message, messageLocalisations, messageLocalisation } = error
      const camelCaseLocale = camelCase(i18n.locale)

      if (messageLocalisation) {
        notification.error(messageLocalisation)
      } else if (
        messageLocalisations &&
        messageLocalisations[camelCaseLocale]
      ) {
        // public api sends back the message localised in all supported languages
        notification.error(messageLocalisations[camelCaseLocale])
      } else {
        notification.error(
          message ? message : t`There was an API error. Please try again later.`
        )
      }

      console.error(error)
    })
  }
}

export const apolloClientPrivate = makeApolloClient(
  {
    link: link.concat(
      ApolloLink.from([
        new ApolloLink((operation, forward) => {
          // @ts-expect-error
          apolloClientPrivate.inFlight += 1
          return forward(operation).map((result) => {
            logPrivate(`${operation.operationName}`, result.data)
            // @ts-expect-error
            apolloClientPrivate.inFlight -= 1

            return result
          })
        }),
        onError(
          ({
            graphQLErrors,
            networkError,
            operation,
            forward
          }: ErrorResponse) => {
            handleApiErrors(graphQLErrors, operation)
            if (networkError) {
              if ((networkError as ServerError).statusCode === 401) {
                const data = apolloClientPublic.readQuery<SessionQuery>({
                  query: SessionDocument,
                  variables: {
                    sid: null
                  }
                })
                if (data?.session) {
                  localStorage.setItem(
                    LOGIN_PAGE_ERROR_LS_KEY,
                    (networkError as ServerError).result?.message ??
                      t`Your session has expired.`
                  )
                }
                // this should only happen when session expired, in this case we need to go to login screen and the easiest bulletproof way is to reload whole page. Another nice thing is that the current URL is kept intact, so after login user can pick up where they left off
                location.reload()
              } else {
                let eventId = ''
                if (environment.isDevelopment()) {
                  console.error(`[Network error]:`, networkError)
                } else {
                  let error = networkError
                  if (networkError instanceof ProgressEvent) {
                    error = new Error('Network error')
                    // @ts-expect-error
                    error.operation = operation
                  }
                  eventId = captureException(error)
                  notification.error(t`Network error, error ID: ${eventId}`)
                }
                notification.error(t`Network error, error ID: ${eventId}`)
              }
            }

            // @ts-expect-error TODO make a separate apollo client for the TopicEditPage and it's children and only do this hack there
            apolloClientPrivate.lastFailedOperation = operation // a way to propagate last failed operation into a component, requested here from apollo so that this hack is not needed: https://github.com/apollographql/apollo-client/issues/6091
            forward(operation)
          }
        ),
        new BatchHttpLink({
          uri: `/graphql/private`,
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          // @ts-expect-error 9/11/2020
          fetch,
          batchMax: 25
        })
      ])
    ),
    connectToDevTools: !isSSR
  },
  gqlPrivateSchemaFragments
)
// @ts-expect-error
apolloClientPrivate.lastFailedOperation = null // needed for TopicEditPage
// @ts-expect-error
apolloClientPrivate.inFlight = 0 // needed for TopicEditPage

export const apolloClientPublic = makeApolloClient({
  link: ApolloLink.from([
    new ApolloLink((operation, forward) => {
      return forward(operation).map((result) => {
        logPublic(`${operation.operationName}`, result.data)

        return result
      })
    }),
    onError(
      ({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
        handleApiErrors(graphQLErrors, operation)
        if (networkError) {
          let eventId = ''
          if (environment.isDevelopment()) {
            console.error(`[Network error]:`, networkError)
          } else {
            let error = networkError
            if (networkError instanceof ProgressEvent) {
              error = new Error('Network error')
              // @ts-expect-error
              error.operation = operation
            }
            eventId = captureException(error)
            notification.error(t`Network error, error ID: ${eventId}`)
          }
          notification.error(t`Network error, error ID: ${eventId}`)
        }

        forward(operation)
      }
    ),
    new BatchHttpLink({
      uri: '/graphql/public',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      // @ts-expect-error 9/11/2020
      fetch,
      batchMax: 5
    })
  ]),
  connectToDevTools: !isSSR
})

if (process.env.LOOOP_ENVIRONMENT !== 'dev' && !isSSR) {
  // for dev, we actually want to debug the error
  window.onunhandledrejection = async (error) => {
    if (error.reason && error.reason.message) {
      if (
        error.reason.message.match(/Error writing result to store for query/)
      ) {
        /**
         * THIS is nasty and hacky, but it's the only mitigation for the apollo-client problem that we've got.
         * Issue is outlined here: https://github.com/apollographql/apollo-client/issues/2510#issuecomment-497869566
         */
        await apolloClientPrivate.cache.reset()
        const rootEl = document.getElementById('looopAppRoot') as HTMLDivElement

        console.log('apollo caches reset, remounting the app')
        ReactDOM.unmountComponentAtNode(rootEl)
        const { renderLooopApp } = await import('../components/LooopApp')
        renderLooopApp()
      }
    }
  }
}
